import React, { useEffect } from 'react'
import { connect } from 'react-redux'
import type { ConnectedProps } from 'react-redux'
import { useNavigate, useParams } from 'react-router-dom'
import type { Location } from 'react-router-dom'
import type { PlaylistId } from 'types/playlist'
import { isAggregatedSearchTrack } from '@disco/utils/tracks'
import { appendTracksToPlaylist } from '@disco/state-management/actions/ExplorerActions'
import { loadBatch } from '@disco/state-management/actions/PlaylistTrackActions'
import {
  activateShortlist,
  insertSelectedItems,
} from '@disco/state-management/actions/ShortlistActions'
import mixpanel from '@disco/api/mixpanel'
import api from '@disco/api/server'
import {
  TRACK_PLAYLISTED_FROM_SIM_SEARCH,
  TRACK_PLAYLISTED_FROM_SIM_TAB,
} from '@disco/constants/EventTypes'
import '@disco/state-management/reducers/playlists'
import { getPlaylists } from '@disco/state-management/selectors/playlists'
import { getShortlists } from '@disco/state-management/selectors/shortlists'
import { getTrack } from '@disco/state-management/selectors/tracks'
import DocumentHeadTags from 'components/document-head-tags'
import AddToPlaylistModal from 'components/modals/track/add-to-playlist'
import { getRoutingPreviousLocation } from '@disco/state-management/selectors/routing'
import { getModalBestReturnTo } from '@disco/utils/modals'
import type { RootState } from 'store/configureStore'

interface OwnProps {
  params: {
    trackId: string
  }
}

function mapStateToProps(state: RootState, props: OwnProps) {
  return {
    track: getTrack(state, {
      id: parseInt(props.params.trackId, 10),
    }),
    playlists: getPlaylists(state),
    shortlists: getShortlists(state),
    previousLocation: getRoutingPreviousLocation(state),
  }
}

const connector = connect(mapStateToProps, {
  appendTracksToPlaylist,
  insertSelectedItems,
  activateShortlist,
  loadBatch,
})

type ReduxProps = ConnectedProps<typeof connector>

type Props = ReduxProps & {
  location: Location
  previousLocation: Location
} & OwnProps

const AddToPlaylist = ({
  playlists,
  track,
  appendTracksToPlaylist,
  loadBatch,
  shortlists,
  insertSelectedItems,
  activateShortlist,
  location,
  previousLocation,
}: Props) => {
  const navigate = useNavigate()
  useEffect(() => {
    // fetch track details if we track playlists were not loaded previously
    if (!track) return
    api
      .getTrack(track.id)
      .then(track => loadBatch([track]))
      .catch(() => {})
  }, [track?.id, loadBatch])

  if (!track) return null

  const _onClose = () => {
    navigate(getModalBestReturnTo(location, previousLocation))
  }

  const _onAddToPlaylist = (playlistIs: PlaylistId[]) => {
    const playlistId = playlistIs[0]

    _onClose()

    const existingShortlist = shortlists.find(s => s.playlist_id === playlistId)

    if (existingShortlist) {
      insertSelectedItems(existingShortlist.id, [track])
      activateShortlist(existingShortlist.id)
    } else {
      appendTracksToPlaylist(playlists[playlistId], [track])
    }

    if (location?.state?.type === TRACK_PLAYLISTED_FROM_SIM_TAB) {
      mixpanel.sendPureEvent(
        'Track playlisted from Track Panel Similar Tracks',
        {
          'Track panel': location.state?.panelId,
          'Track playlisted': track.id,
        },
      )
    }

    if (location?.state?.type === TRACK_PLAYLISTED_FROM_SIM_SEARCH) {
      mixpanel.sendPureEvent('Track playlisted from Similarity Search by URL', {
        URL: location.state?.url,
        'Track playlisted': track.id,
        // Properties for event #4 from https://disco-team.atlassian.net/browse/DISCO-5985
        'Track ID': track.id,
        Catalog: track.origin?.source_library?.id,
        'Catalog Business': track.origin?.business?.id,
        ...(isAggregatedSearchTrack(track)
          ? {
              Context: 'Aggregated search',
            }
          : {}),
      })
    }
  }

  return (
    <>
      <DocumentHeadTags title="Add to playlist | DISCO" />
      <AddToPlaylistModal
        shortlists={shortlists}
        track={track}
        onClose={_onClose}
        onSubmit={_onAddToPlaylist}
      />
    </>
  )
}

const ConnectedAddToPlaylist = connector(AddToPlaylist)

// Wrapper component to provide router params to the connected component
const AddToPlaylistWithRouter = (props: any) => {
  const params = useParams<{ trackId: string }>()

  return <ConnectedAddToPlaylist {...props} params={params} />
}

export default AddToPlaylistWithRouter
